import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Anticipation.css";

const Anticipation = () => {
  const navigate = useNavigate();

  const [service, setService] = useState("");
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deviceError, setDeviceError] = useState(false);

  // ==========================================
  // SCROLL TO TOP
  // ==========================================
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // ==========================================
  // DEVICE CHECK
  // Phone only is blocked.
  // Tablets, laptops, and desktops are allowed.
  // ==========================================
  const isMobilePhone = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    const isApplePhone = /iPhone|iPod/i.test(ua);
    const isAndroidPhone = /Android/i.test(ua) && /Mobile/i.test(ua);
    const isWindowsPhone = /Windows Phone/i.test(ua);

    return isApplePhone || isAndroidPhone || isWindowsPhone;
  };

  // ==========================================
  // ACCESS CHECK + SERVICE
  // ==========================================
  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      const email = localStorage.getItem("user_email");
      const authed = localStorage.getItem("rg_auth") === "1";

      if (!authed || !email) {
        navigate("/dashboard-login");
        return;
      }

      try {
        const res = await axios.get("/check-access", {
          params: {
            email,
            test: "anticipation",
          },
        });

        console.log("Anticipation API:", res.data);

        if (!res.data.allowed) {
          if (isMounted) {
            setHasAccess(false);
          }
          return;
        }

        const userService =
          res.data.service ||
          res.data.metadata?.service ||
          res.data.user?.metadata?.service ||
          localStorage.getItem("service") ||
          "";

        if (isMounted) {
          setService(userService);
          setHasAccess(true);
        }
      } catch (err) {
        console.error("Anticipation access check failed:", err);

        if (isMounted) {
          setHasAccess(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ==========================================
  // SERVICE IDENTIFICATION
  // Ignite + Ascend = Existing content
  // Vision Ignite = Separate content
  // ==========================================
  const normalizedService = service.trim().toLowerCase();

  const isVisionIgnite =
    normalizedService === "forge your peak - vision ignite";

  const isIgniteOrAscend =
    normalizedService === "forge your peak - ignite" ||
    normalizedService === "forge your peak - ascend";

  // ==========================================
  // RETURN HOME
  // ==========================================
  const returnHome = () => {
    if (isVisionIgnite) {
      navigate("/measure");
    } else if (isIgniteOrAscend) {
      navigate("/ignite/dashboard");
    } else {
      navigate("/");
    }
  };

  // ==========================================
  // START TEST
  // ==========================================
  const handleStartTest = () => {
    if (isMobilePhone()) {
      setDeviceError(true);
      return;
    }

    navigate("/anticipation/play");
  };

  // ==========================================
  // LOADING
  // ==========================================
  if (loading || hasAccess === null) {
    return (
      <div className="rg-login-wrapper">
        <p style={{ textAlign: "center" }}>Checking access...</p>
      </div>
    );
  }

  // ==========================================
  // ACCESS DENIED
  // ==========================================
  if (hasAccess === false) {
    return (
      <div className="access-denied-container">
        <h2>🚫 Access Denied</h2>

        <p>You don't have access to this test.</p>

        <button type="button" onClick={() => navigate("/")}>
          Go Back
        </button>
      </div>
    );
  }

  // ==========================================
  // SERVICE-BASED CONTENT
  // ==========================================
  const pageTitle = isVisionIgnite
    ? "Anticipation Assessment"
    : "Anticipation Test";

  const pageDescription = isVisionIgnite
    ? "Anticipation measures your ability to predict what will happen next using visual cues and movement patterns. This assessment focuses on visual prediction, attention, and response timing."
    : 'Anticipation measures your ability to predict what will happen next based on visual cues and movement patterns. It trains the brain to "Observe → Predict → React."';

  return (
    <div className="rg-login-wrapper">
      {/* ==========================================
          RETURN HOME
      ========================================== */}
      <div className="return-home-wrapper">
        <button
          type="button"
          onClick={returnHome}
          className="return-home-btn"
        >
          ← Return Home
        </button>
      </div>

      {/* ==========================================
          MOBILE BLOCK POPUP
      ========================================== */}
      {deviceError && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "40px 36px",
              textAlign: "center",
              maxWidth: "340px",
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                fontSize: "56px",
                marginBottom: "16px",
              }}
            >
              📱
            </div>

            <h2
              style={{
                margin: "0 0 10px",
                fontSize: "22px",
                color: "#1a1a2e",
              }}
            >
              Mobile Not Supported
            </h2>

            <p
              style={{
                color: "#666",
                fontSize: "15px",
                lineHeight: "1.6",
                marginBottom: "28px",
              }}
            >
              This test requires a larger screen.
              <br />
              Please use a tablet, laptop, or desktop computer.
            </p>

            <button
              type="button"
              onClick={() => setDeviceError(false)}
              style={{
                padding: "12px 32px",
                background: "#f0f0f0",
                color: "#444",
                border: "none",
                borderRadius: "10px",
                fontSize: "15px",
                cursor: "pointer",
                width: "100%",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* ==========================================
          HEADER SECTION
      ========================================== */}
      <div className="reaction-header-card">
        <h2 className="reaction-title">{pageTitle}</h2>

        <p className="reaction-subtitle">{pageDescription}</p>

        <div className="reaction-pill">
          Test type: <strong>Prediction → Decision making</strong>
        </div>

        <hr className="reaction-divider" />

        {/* ==========================================
            WHY SECTION
        ========================================== */}
        <div className="reaction-why-card">
          <h3 className="why-title">
            {isVisionIgnite
              ? "Why Anticipation Matters"
              : "Why anticipation matters in sport"}
          </h3>

          {isVisionIgnite ? (
            <>
              <p className="why-description">
                Anticipation involves using visual cues to predict what may
                happen next. It helps you prepare a response before an event is
                completed and supports efficient visual decision-making.
              </p>

              <div className="why-tags">
                <span>Visual Prediction</span>
                <span>Pattern Recognition</span>
                <span>Decision Making</span>
                <span>Response Timing</span>
                <span>Attention</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Visual prediction:</strong> Use visual cues to
                  anticipate the next event.
                </li>

                <li>
                  <strong>Pattern recognition:</strong> Observe movement
                  patterns and identify relevant changes.
                </li>

                <li>
                  <strong>Decision making:</strong> Select an appropriate
                  response based on the information available.
                </li>

                <li>
                  <strong>Response timing:</strong> Practise selecting the
                  predicted position as accurately as possible.
                </li>
              </ul>
            </>
          ) : (
            <>
              <p className="why-description">
                In sports, elite athletes don't just react — they anticipate.
                Whether it's predicting a pass, reading an opponent's move, or
                timing a shot, anticipation gives players a critical advantage
                by reducing reaction time and improving decisions.
              </p>

              <div className="why-tags">
                <span>Faster decision making</span>
                <span>Better game intelligence</span>
                <span>Improved reaction timing</span>
                <span>Predict opponent movement</span>
                <span>Enhanced performance under pressure</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ==========================================
          HOW GAME WORKS
      ========================================== */}
      <div className="how-card">
        <h2 className="how-title">How the game works</h2>

        <ul className="how-list">
          <li>A moving object or pattern will appear on the screen.</li>

          <li>It may disappear or change direction suddenly.</li>

          <li>
            Your task is to <strong>predict where it will appear next</strong>.
          </li>

          <li>Select or click the correct predicted position.</li>

          <li>
            You will get <strong>multiple rounds</strong>, and your accuracy and
            timing will be recorded.
          </li>

          <li>Faster and correct predictions increase your score.</li>
        </ul>

        <p className="how-note">
          ⚠️ This test works on tablet, laptop, and desktop. Mobile phones are
          not supported.
        </p>
      </div>

      {/* ==========================================
          START TEST
      ========================================== */}
      <div className="start-test-section">
        <h2>
          {isVisionIgnite
            ? "Ready to Assess Your Anticipation Skills?"
            : "Ready to test your anticipation skills?"}
        </h2>

        <button
          type="button"
          className="start-test-btn"
          onClick={handleStartTest}
        >
          Start Test
        </button>
      </div>
    </div>
  );
};

export default Anticipation;